import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

export default function GestioneMacchine() {
  const navigate = useNavigate();

  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(true);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingDepartment, setSavingDepartment] = useState(false);

  const [editingMachineId, setEditingMachineId] = useState(null);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Conferma',
    danger: false,
    onConfirm: null,
  });

  const [form, setForm] = useState({
    nome: '',
    sede: '',
    customerUserId: '',
    departmentRef: '',
    matricola: '',
    note: '',
  });

  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    sede: '',
    customerUserId: '',
    notes: '',
  });

  const getId = (value) => {
    if (!value) return '';
    return typeof value === 'string' ? value : value._id || '';
  };

  const getClientLabel = (client) => {
    return (
      `${client?.nome || ''} ${client?.cognome || ''}`.trim() ||
      client?.username ||
      client?.matricola ||
      'Cliente'
    );
  };

  const filteredDepartmentsForMachine = useMemo(() => {
    return departments.filter((department) => {
      if (department.isActive === false) return false;
      return getId(department.customerUserId) === form.customerUserId;
    });
  }, [departments, form.customerUserId]);

  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);

      const res = await authFetch('/api/departments');
      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore nel recupero reparti');
      }

      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore fetch departments:', err);
      setError(err?.message || 'Impossibile recuperare i reparti.');
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchMachines = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await authFetch('/api/machines');
      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore nel recupero delle macchine');
      }

      setMachines(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore fetch machines:', err);
      setError(err?.message || 'Impossibile recuperare le macchine.');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      setLoadingClients(true);

      const res = await authFetch('/api/users');
      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.error || 'Errore nel recupero utenti');
      }

      const clientsOnly = (Array.isArray(data) ? data : []).filter(
        (u) => u.role === 'cliente'
      );

      setClients(clientsOnly);
    } catch (err) {
      console.error('Errore fetch clients:', err);
      setError(err?.message || 'Impossibile recuperare i clienti.');
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    fetchMachines();
    fetchClients();
    fetchDepartments();
  }, []);

  const resetMachineForm = () => {
    setForm({
      nome: '',
      sede: '',
      customerUserId: '',
      departmentRef: '',
      matricola: '',
      note: '',
    });
    setEditingMachineId(null);
  };

  const resetDepartmentForm = () => {
    setDepartmentForm({
      name: '',
      sede: '',
      customerUserId: '',
      notes: '',
    });
    setEditingDepartmentId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'customerUserId' ? { departmentRef: '' } : {}),
    }));
  };

  const handleDepartmentChange = (e) => {
    const { name, value } = e.target;

    setDepartmentForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitMachine = async (e) => {
    e.preventDefault();

    if (!form.nome.trim()) return;

    try {
      setSaving(true);
      setError('');

      const isEdit = Boolean(editingMachineId);
      const url = isEdit ? `/api/machines/${editingMachineId}` : '/api/machines';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          customerUserId: form.customerUserId || null,
          departmentRef: form.departmentRef || null,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(
          data?.message ||
            (isEdit
              ? 'Errore aggiornamento macchina'
              : 'Errore salvataggio macchina')
        );
      }

      resetMachineForm();
      await fetchMachines();
    } catch (err) {
      console.error('Errore submit machine:', err);
      setError(err?.message || 'Impossibile salvare la macchina.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditMachine = (machine) => {
    setEditingMachineId(machine._id);
    setForm({
      nome: machine.nome || '',
      sede: machine.sede || '',
      customerUserId: getId(machine.customerUserId),
      departmentRef: getId(machine.departmentRef),
      matricola: machine.matricola || '',
      note: machine.note || '',
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const openConfirmModal = ({ title, message, confirmText, danger, onConfirm }) => {
  setConfirmModal({
    open: true,
    title,
    message,
    confirmText: confirmText || 'Conferma',
    danger: Boolean(danger),
    onConfirm,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({
      ...prev,
      open: false,
      onConfirm: null,
    }));
  };

  const runConfirmedAction = async () => {
    const action = confirmModal.onConfirm;
    closeConfirmModal();

    if (typeof action === 'function') {
      await action();
    }
  };


  const handleDeleteMachine = (machine) => {
    openConfirmModal({
      title: 'Eliminare macchina?',
      message: `Sei sicuro di voler eliminare la macchina "${machine.nome}"? Questa operazione non eliminerà lo storico già salvato, ma rimuoverà la macchina dall’elenco.`,
      confirmText: 'Elimina',
      danger: true,
      onConfirm: async () => {
        try {
          setError('');

          const res = await authFetch(`/api/machines/${machine._id}`, {
            method: 'DELETE',
          });

          const data = await res.json().catch(() => null);

          if (!res || !res.ok) {
            throw new Error(data?.message || 'Errore eliminazione macchina');
          }

          if (editingMachineId === machine._id) {
            resetMachineForm();
          }

          await fetchMachines();
        } catch (err) {
          console.error('Errore delete machine:', err);
          setError(err?.message || 'Impossibile eliminare la macchina.');
        }
      },
    });
  };

  const handleSubmitDepartment = async (e) => {
    e.preventDefault();

    if (!departmentForm.name.trim()) {
      setError('Il nome reparto è obbligatorio.');
      return;
    }

    if (!departmentForm.customerUserId) {
      setError('Seleziona un cliente per il reparto.');
      return;
    }

    try {
      setSavingDepartment(true);
      setError('');

      const isEdit = Boolean(editingDepartmentId);
      const url = isEdit
        ? `/api/departments/${editingDepartmentId}`
        : '/api/departments';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: departmentForm.name,
          sede: departmentForm.sede,
          customerUserId: departmentForm.customerUserId,
          notes: departmentForm.notes,
          isActive: true,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(
          data?.message ||
            (isEdit
              ? 'Errore aggiornamento reparto'
              : 'Errore salvataggio reparto')
        );
      }

      resetDepartmentForm();
      await fetchDepartments();
      await fetchMachines();
    } catch (err) {
      console.error('Errore submit department:', err);
      setError(err?.message || 'Impossibile salvare il reparto.');
    } finally {
      setSavingDepartment(false);
    }
  };

  const handleEditDepartment = (department) => {
    setEditingDepartmentId(department._id);
    setDepartmentForm({
      name: department.name || '',
      sede: department.sede || '',
      customerUserId: getId(department.customerUserId),
      notes: department.notes || '',
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteDepartment = (department) => {
    openConfirmModal({
      title: 'Disattivare reparto?',
      message: `Vuoi disattivare il reparto "${department.name}"? Le macchine già collegate manterranno lo storico e il reparto non sarà più selezionabile per nuove associazioni.`,
      confirmText: 'Disattiva',
      danger: true,
      onConfirm: async () => {
        try {
          setError('');

          const res = await authFetch(`/api/departments/${department._id}`, {
            method: 'DELETE',
          });

          const data = await res.json().catch(() => null);

          if (!res || !res.ok) {
            throw new Error(data?.message || 'Errore disattivazione reparto');
          }

          if (editingDepartmentId === department._id) {
            resetDepartmentForm();
          }

          await fetchDepartments();
          await fetchMachines();
        } catch (err) {
          console.error('Errore delete department:', err);
          setError(err?.message || 'Impossibile disattivare il reparto.');
        }
      },
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-10 space-y-6">
        <Card className="shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Gestione macchine
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Crea reparti, associa macchine ai clienti e prepara la struttura
                per gli allarmi.
              </p>
            </div>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition"
            >
              Torna alla dashboard
            </button>
          </div>
        </Card>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4">
            {error}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
          {/* NUOVO REPARTO */}
          <Card className="shadow-md p-6 h-full">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingDepartmentId ? 'Modifica reparto' : 'Nuovo reparto'}
              </h2>

              {editingDepartmentId && (
                <button
                  type="button"
                  onClick={resetDepartmentForm}
                  className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-xs font-medium transition"
                >
                  Annulla modifica
                </button>
              )}
            </div>

            <form onSubmit={handleSubmitDepartment} className="space-y-4">
              <select
                name="customerUserId"
                value={departmentForm.customerUserId}
                onChange={handleDepartmentChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                disabled={loadingClients}
                required
              >
                <option value="">Seleziona cliente</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {getClientLabel(client)}
                  </option>
                ))}
              </select>

              <input
                type="text"
                name="name"
                value={departmentForm.name}
                onChange={handleDepartmentChange}
                placeholder="Nome reparto"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
                required
              />

              <input
                type="text"
                name="sede"
                value={departmentForm.sede}
                onChange={handleDepartmentChange}
                placeholder="Sede reparto"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
              />

              <textarea
                name="notes"
                value={departmentForm.notes}
                onChange={handleDepartmentChange}
                placeholder="Note reparto"
                className="w-full min-h-[100px] border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
              />

              <button
                type="submit"
                disabled={savingDepartment}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium transition disabled:opacity-60"
              >
                {savingDepartment
                  ? 'Salvataggio...'
                  : editingDepartmentId
                  ? 'Salva reparto'
                  : 'Aggiungi reparto'}
              </button>
            </form>
          </Card>

          {/* ELENCO REPARTI */}
          <Card className="shadow-md p-6 h-full">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Elenco reparti
            </h2>

            {loadingDepartments ? (
              <p className="text-sm text-gray-500">Caricamento reparti...</p>
            ) : departments.length === 0 ? (
              <p className="text-sm text-gray-500">Nessun reparto presente.</p>
            ) : (
              <div className="space-y-3 max-h-[430px] overflow-auto pr-1">
                {departments.map((department) => (
                  <div
                    key={department._id}
                    className={`border rounded-xl p-4 ${
                      department.isActive === false
                        ? 'border-gray-200 bg-gray-100 opacity-70'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-800">
                          {department.name}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                          {department.sede || '-'}
                        </p>

                        <p className="text-sm text-gray-600 mt-2">
                          Cliente:{' '}
                          <strong>
                            {department.customerUserId
                              ? getClientLabel(department.customerUserId)
                              : '-'}
                          </strong>
                        </p>

                        {department.isActive === false && (
                          <p className="text-xs font-semibold text-red-600 mt-2">
                            Reparto disattivato
                          </p>
                        )}

                        <p className="text-sm text-gray-500 mt-2">
                          {department.notes || 'Nessuna nota'}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditDepartment(department)}
                          className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-xs font-medium transition"
                        >
                          Modifica
                        </button>

                        {department.isActive !== false && (
                          <button
                            type="button"
                            onClick={() => handleDeleteDepartment(department)}
                            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium transition"
                          >
                            Disattiva
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* NUOVA MACCHINA */}
          <Card className="shadow-md p-6 h-full">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {editingMachineId ? 'Modifica macchina' : 'Nuova macchina'}
              </h2>

              {editingMachineId && (
                <button
                  type="button"
                  onClick={resetMachineForm}
                  className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-xs font-medium transition"
                >
                  Annulla modifica
                </button>
              )}
            </div>

            <form onSubmit={handleSubmitMachine} className="space-y-4">
              <input
                type="text"
                name="nome"
                value={form.nome}
                onChange={handleChange}
                placeholder="Nome macchina"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400"
                required
              />

              <select
                name="customerUserId"
                value={form.customerUserId}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                disabled={loadingClients}
              >
                <option value="">Seleziona cliente</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {getClientLabel(client)}
                  </option>
                ))}
              </select>

              <input
                type="text"
                name="sede"
                value={form.sede}
                onChange={handleChange}
                placeholder="Sede macchina"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400"
              />

              <select
                name="departmentRef"
                value={form.departmentRef}
                onChange={handleChange}
                disabled={!form.customerUserId || loadingDepartments}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400 bg-white disabled:bg-gray-100"
              >
                <option value="">
                  {!form.customerUserId
                    ? 'Seleziona prima un cliente'
                    : filteredDepartmentsForMachine.length === 0
                    ? 'Nessun reparto disponibile'
                    : 'Seleziona reparto'}
                </option>

                {filteredDepartmentsForMachine.map((department) => (
                  <option key={department._id} value={department._id}>
                    {department.name}
                    {department.sede ? ` - ${department.sede}` : ''}
                  </option>
                ))}
              </select>

              <input
                type="text"
                name="matricola"
                value={form.matricola}
                onChange={handleChange}
                placeholder="Matricola macchina"
                className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400"
              />

              <textarea
                name="note"
                value={form.note}
                onChange={handleChange}
                placeholder="Note"
                className="w-full min-h-[120px] border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-400"
              />

              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium transition disabled:opacity-60"
              >
                {saving
                  ? 'Salvataggio...'
                  : editingMachineId
                  ? 'Salva modifiche'
                  : 'Aggiungi macchina'}
              </button>
            </form>
          </Card>

          {/* ELENCO MACCHINE */}
          <Card className="shadow-md p-6 h-full">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Elenco macchine
            </h2>

            {loading ? (
              <p className="text-sm text-gray-500">Caricamento macchine...</p>
            ) : machines.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nessuna macchina presente.
              </p>
            ) : (
              <div className="space-y-4 max-h-[430px] overflow-auto pr-1">
                {machines.map((machine) => (
                  <div
                    key={machine._id}
                    className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">
                          {machine.nome}
                        </h3>

                        <p className="text-sm text-gray-500 mt-1">
                          {machine.sede || '-'} ·{' '}
                          {machine.departmentRef?.name ||
                            machine.reparto ||
                            '-'}
                        </p>

                        <p className="text-sm text-gray-600 mt-2">
                          Cliente:{' '}
                          <strong>
                            {machine.customerUserId
                              ? getClientLabel(machine.customerUserId)
                              : '-'}
                          </strong>
                        </p>

                        <p className="text-sm text-gray-600">
                          Matricola:{' '}
                          <strong>{machine.matricola || '-'}</strong>
                        </p>

                        <p className="text-sm text-gray-500 mt-2">
                          {machine.note || 'Nessuna nota'}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditMachine(machine)}
                          className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-xs font-medium transition"
                        >
                          Modifica
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteMachine(machine)}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium transition"
                        >
                          Elimina
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <Card className="w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-6">
              <div
                className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full text-xl font-bold ${
                  confirmModal.danger
                    ? 'bg-red-100 text-red-600'
                    : 'bg-cyan-100 text-cyan-600'
                }`}
              >
                !
              </div>

              <h3 className="text-xl font-bold text-gray-900 text-center">
                {confirmModal.title}
              </h3>

              <p className="mt-3 text-sm text-gray-600 text-center leading-6">
                {confirmModal.message}
              </p>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={closeConfirmModal}
                  className="flex-1 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 transition"
                >
                  Annulla
                </button>

                <button
                  type="button"
                  onClick={runConfirmedAction}
                  disabled={saving || savingDepartment}
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
                    confirmModal.danger
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-cyan-600 hover:bg-cyan-700'
                  }`}
                >
                  {saving || savingDepartment ? 'Attendere...' : confirmModal.confirmText}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}